// The open invites Laila lists (lailaChat's list_open_invites tool): what
// the card holds, how each invite reads, and the user's resends and cancels
// on it. The mobile app has the same logic (screens/AIChat/components/cards).
import { ChatCardTone } from '../../../../components/Chat/kit';
import { displayPhone } from './inviteDraft';

export type OpenInviteStatus =
  | 'pending'
  | 'sent'
  | 'not_sent'
  | 'not_accepted'
  | 'expired'
  | 'partially_accepted';

// What the user did to one invite from the card.
export type InviteRowState = 'resending' | 'resent' | 'cancelling' | 'cancelled' | 'failed';

export interface InviteListRow {
  id: string;
  name?: string;
  phone?: string;
  email?: string;
  status: OpenInviteStatus;
  type: 'worker' | 'staff';
  // "Bricklayers, Riverside Wharf".
  where?: string;
  invitedBy?: string;
  invitedAt: string;
  state?: InviteRowState;
  // Why the last resend or cancel failed.
  error?: string;
}

export interface InviteListData {
  companyId: string;
  // All open invites, of which `invites` are the newest.
  total: number;
  invites: InviteListRow[];
}

export const INVITE_STATUS: Record<OpenInviteStatus, { label: string; tone: ChatCardTone }> = {
  pending: { label: 'Pending', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'neutral' },
  not_sent: { label: 'Not sent', tone: 'warning' },
  not_accepted: { label: 'Not accepted', tone: 'error' },
  expired: { label: 'Expired', tone: 'error' },
  partially_accepted: { label: 'Partly accepted', tone: 'brand' },
};

export type InviteFilter = 'all' | 'expired' | 'sent' | 'not_sent';

export const INVITE_FILTERS: { value: InviteFilter; label: string }[] = [
  { value: 'all', label: 'All open' },
  { value: 'expired', label: 'Expired' },
  { value: 'sent', label: 'Sent' },
  { value: 'not_sent', label: 'Not sent' },
];

const isOpen = (row: InviteListRow) => row.state !== 'cancelled';

export const filterInvites = (rows: InviteListRow[], filter: InviteFilter) =>
  filter === 'all' ? rows : rows.filter(row => row.status === filter);

// How many each filter shows; a filter with none is left off the card.
export const filterCounts = (rows: InviteListRow[]) =>
  INVITE_FILTERS.reduce(
    (counts, { value }) => ({ ...counts, [value]: filterInvites(rows.filter(isOpen), value).length }),
    {} as Record<InviteFilter, number>
  );

// Who it is to: their name, else their number, else their email.
export const inviteeLabel = (row: InviteListRow) =>
  row.name || (row.phone ? displayPhone(row.phone) : '') || row.email || 'Someone new';

export const initials = (name?: string) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

// "today", "yesterday", "9 days ago": an invite's age is what matters, not its date.
export const invitedAgo = (iso: string, now = new Date()) => {
  const day = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((day(now) - day(new Date(iso))) / 86400000);
  if (days <= 0) {
    return 'today';
  }
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

export const canAct = (row: InviteListRow) =>
  row.state !== 'cancelled' && row.state !== 'cancelling' && row.state !== 'resending';

// The expired ones a "Resend all" would send again.
export const expiredToResend = (rows: InviteListRow[]) =>
  rows.filter(row => row.status === 'expired' && canAct(row) && row.state !== 'resent');

export const updateRow = (
  data: InviteListData,
  id: string,
  patch: Partial<InviteListRow>
): InviteListData => ({
  ...data,
  invites: data.invites.map(row => (row.id === id ? { ...row, ...patch } : row)),
});

// The Vyce API's reason, when it gave one in words.
export const inviteActionError = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) {
    return detail;
  }
  if (!error?.response) {
    return 'No connection. Try again when you’re back online.';
  }
  return 'That didn’t work. Try again in a minute.';
};
